import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button } from '#pplx/frontend/aether/components/Button/index.js';
import { DropdownMenu } from '#pplx/frontend/aether/components/DropdownMenu/index.js';
import { useAskInputActions, useAskInputState, } from '#pplx/frontend/apps/ask/ask-input/ask-input-provider/components/AskInputProviderShared.js';
import { dedupeWorkspaceDirectories, mergeRecentDirectories, normalizeWorkspaceDirectory, RECENT_WORKSPACE_DIRECTORIES_STORAGE_KEY, SELECTED_WORKSPACE_DIRECTORIES_STORAGE_KEY, workspaceDirectoryNameFromPath, } from '#pplx/frontend/apps/ask/ask-input/ask-input-types/index.js';
import { plusMenuItemSelectedEvent } from '#pplx/frontend/apps/ask/events/ask-input-plus-menu/analytics.js';
import { useManagedFolders, useNativeBridgeActions, } from '#pplx/frontend/apps/ask/native-bridges/store.js';
import { useSession } from '#pplx/frontend/auth/useSession.js';
import { useLocalStorage } from '#pplx/frontend/browser-storage/hooks/useLocalStorage.js';
import { useInstrumentation } from '#pplx/frontend/framework/instrumentation/useInstrumentation.js';
import { $icon } from '#pplx/frontend/icons/icon-sprite.js';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
const WORKSPACE_FOLDER_CHIP_MAX_WIDTH_PX = 150;
const sameDirectoryPaths = (a, b) => a.length === b.length && a.every((dir, index) => dir.path === b[index]?.path);
const useExplicitWorkspaceDirectories = () => {
    const { workspaceDirectories } = useAskInputState();
    const managedFolders = useManagedFolders();
    return useMemo(() => {
        if (managedFolders.length === 0)
            return workspaceDirectories;
        const managedPaths = new Set(managedFolders.map((f) => f.path));
        return workspaceDirectories.filter((d) => !managedPaths.has(d.path));
    }, [workspaceDirectories, managedFolders]);
};
const useRecentWorkspaceDirectories = () => useLocalStorage(RECENT_WORKSPACE_DIRECTORIES_STORAGE_KEY, []);
const useSelectedWorkspaceDirectories = () => useLocalStorage(SELECTED_WORKSPACE_DIRECTORIES_STORAGE_KEY, []);
/**
 * New-session persistence bridge. Thread inputs intentionally do not mount
 * this component, so thread metadata and follow-up edits do not leak into the
 * global folder selection used by new sessions.
 */
export const ComputerWorkspaceFolderSelectionPersistence = () => {
    const hydratedRef = useRef(false);
    const explicitDirectories = useExplicitWorkspaceDirectories();
    const { setWorkspaceDirectories } = useAskInputActions();
    const { status } = useSession();
    const [, setSelectedDirectories, getSelectedDirectories] = useSelectedWorkspaceDirectories();
    const [, setRecentDirectories] = useRecentWorkspaceDirectories();
    useEffect(() => {
        if (status === 'unauthenticated') {
            setWorkspaceDirectories([]);
            setSelectedDirectories([]);
            setRecentDirectories([]);
        }
    }, [setRecentDirectories, setSelectedDirectories, setWorkspaceDirectories, status]);
    useEffect(() => {
        if (status === 'unauthenticated')
            return;
        if (!hydratedRef.current) {
            hydratedRef.current = true;
            const persisted = getSelectedDirectories();
            // Restore the persisted selection only into an empty store so we never
            // clobber a thread's own folders or a user's in-session choice. The
            // resulting store change re-runs this effect for the write-through below.
            if (explicitDirectories.length === 0 && persisted.length > 0) {
                setWorkspaceDirectories(persisted.map(normalizeWorkspaceDirectory));
                return;
            }
        }
        const nextSelected = explicitDirectories.map(normalizeWorkspaceDirectory);
        if (!sameDirectoryPaths(getSelectedDirectories(), nextSelected)) {
            setSelectedDirectories(nextSelected);
        }
        if (nextSelected.length > 0) {
            setRecentDirectories((previous) => mergeRecentDirectories(nextSelected, previous));
        }
    }, [
        explicitDirectories,
        getSelectedDirectories,
        setRecentDirectories,
        setSelectedDirectories,
        setWorkspaceDirectories,
        status,
    ]);
    return null;
};
const useWorkspaceDirectorySelection = () => {
    const { setWorkspaceDirectories } = useAskInputActions();
    const { track } = useInstrumentation();
    const selectWorkspaceDirectory = useCallback((dir) => {
        track(plusMenuItemSelectedEvent({
            name: 'plus menu item selected',
            data: { item: 'recent_local_folder' },
        }));
        setWorkspaceDirectories([normalizeWorkspaceDirectory(dir)]);
    }, [setWorkspaceDirectories, track]);
    const clearWorkspaceDirectorySelection = useCallback(() => {
        setWorkspaceDirectories([]);
    }, [setWorkspaceDirectories]);
    return { clearWorkspaceDirectorySelection, selectWorkspaceDirectory };
};
const useHandleAddFolder = () => {
    const { pickDirectories, pickDirectory } = useNativeBridgeActions();
    const { setWorkspaceDirectories } = useAskInputActions();
    const { track } = useInstrumentation();
    const handleAddFolder = useCallback(async () => {
        if (!pickDirectories && !pickDirectory)
            return;
        track(plusMenuItemSelectedEvent({
            name: 'plus menu item selected',
            data: { item: 'local_folder' },
        }));
        try {
            const results = pickDirectories
                ? await pickDirectories()
                : pickDirectory
                    ? await pickDirectory().then((result) => (result ? [result] : []))
                    : [];
            if (results.length === 0)
                return;
            setWorkspaceDirectories([normalizeWorkspaceDirectory(results[0])]);
        }
        catch {
            // Native bridge throws when user cancels the directory picker; no
            // logging since cancellation is routine.
        }
    }, [pickDirectories, pickDirectory, setWorkspaceDirectories, track]);
    return { handleAddFolder, canPickDirectory: Boolean(pickDirectories || pickDirectory) };
};
const WorkspaceFolderMenuItems = ({ showAddFolder }) => {
    useIntl();
    const { handleAddFolder } = useHandleAddFolder();
    if (!showAddFolder)
        return null;
    return (_jsx(DropdownMenu.Item, { leadingAccessory: $icon('folder-plus'), onSelect: handleAddFolder, children: _jsx(FormattedMessage, { defaultMessage: "Select new folder...", description: "Menu item to add a local folder via the directory picker", id: "e4a6a366ee" }) }));
};
const FolderSelectorMenuItems = ({ directories, activePath, unavailablePaths, withTrailingSeparator }) => {
    useIntl();
    const { selectWorkspaceDirectory } = useWorkspaceDirectorySelection();
    if (directories.length === 0)
        return null;
    return (_jsxs(_Fragment, { children: [_jsx(DropdownMenu.InfoItem, { children: _jsx(FormattedMessage, { defaultMessage: "Folders", description: "Label for selectable local workspace folders", id: "157615ab8e" }) }), _jsx(DropdownMenu.RadioGroup, { value: activePath ?? '', onValueChange: (path) => {
                    if (unavailablePaths?.has(path))
                        return;
                    const selectedDir = directories.find((dir) => dir.path === path);
                    if (selectedDir)
                        selectWorkspaceDirectory(selectedDir);
                }, children: directories.map((dir) => {
                    const isUnavailable = unavailablePaths?.has(dir.path) ?? false;
                    return (_jsx(DropdownMenu.RadioItem, { value: dir.path, disabled: isUnavailable, leadingAccessory: $icon('folder-open'), textValue: dir.path, children: _jsx("span", { className: "block max-w-[320px] whitespace-normal break-all text-left", children: dir.path }) }, dir.path));
                }) }), withTrailingSeparator && _jsx(DropdownMenu.Separator, {})] }));
};
const ManagedFolderMenuItems = ({ managedFolders, withTrailingSeparator = true }) => {
    if (managedFolders.length === 0)
        return null;
    return (_jsxs(_Fragment, { children: [managedFolders.map((folder) => (_jsx(DropdownMenu.CheckboxItem, { checked: true, disabled: true, leadingAccessory: $icon('folder-cog'), onCheckedChange: () => { }, textValue: folder.path, children: _jsx("span", { className: "block max-w-[320px] whitespace-normal break-all text-left", children: folder.path }) }, folder.path))), withTrailingSeparator && _jsx(DropdownMenu.Separator, {})] }));
};
const RemoveWorkspaceFolderMenuItem = ({ withTrailingSeparator, }) => {
    useIntl();
    const { clearWorkspaceDirectorySelection } = useWorkspaceDirectorySelection();
    return (_jsxs(_Fragment, { children: [_jsx(DropdownMenu.Item, { leadingAccessory: $icon('x'), onSelect: clearWorkspaceDirectorySelection, children: _jsx(FormattedMessage, { defaultMessage: "Remove folder", description: "Menu item to remove the currently attached local workspace folder", id: "9303c65642" }) }), withTrailingSeparator && _jsx(DropdownMenu.Separator, {})] }));
};
const useFolderSelectorDirectories = (explicitDirectories, recentDirectories) => useMemo(() => dedupeWorkspaceDirectories([...explicitDirectories, ...recentDirectories]), [explicitDirectories, recentDirectories]);
const useStableDirectoriesWhileOpen = (directories, isOpen) => {
    const snapshotRef = useRef(null);
    if (isOpen && snapshotRef.current === null) {
        snapshotRef.current = directories;
    }
    if (!isOpen && snapshotRef.current !== null) {
        snapshotRef.current = null;
    }
    return snapshotRef.current ?? directories;
};
const useUnavailableDirectoryPaths = (directories) => {
    const { checkDirectoriesExist } = useNativeBridgeActions();
    const [availability, setAvailability] = React.useState({});
    const directoryPaths = useMemo(() => directories.map((dir) => dir.path), [directories]);
    const directoryPathsKey = useMemo(() => directoryPaths.join('\u0000'), [directoryPaths]);
    useEffect(() => {
        if (!checkDirectoriesExist || directoryPaths.length === 0) {
            setAvailability({});
            return;
        }
        let isCurrent = true;
        checkDirectoriesExist(directoryPaths)
            .then((nextAvailability) => {
            if (isCurrent)
                setAvailability(nextAvailability);
        })
            .catch(() => {
            if (isCurrent)
                setAvailability({});
        });
        return () => {
            isCurrent = false;
        };
    }, [checkDirectoriesExist, directoryPaths, directoryPathsKey]);
    return useMemo(() => new Set(directoryPaths.filter((path) => availability[path] === false)), [availability, directoryPaths]);
};
const workspaceFolderLabel = (activeDirectories, $t) => {
    if (activeDirectories.length === 0) {
        return $t({
            defaultMessage: 'Folders',
            description: 'Submenu item label for attaching local folders to Computer',
            id: '5c79ea331d',
        });
    }
    if (activeDirectories.length === 1) {
        const directory = activeDirectories[0];
        return 'alias' in directory ? directory.alias : workspaceDirectoryNameFromPath(directory.path);
    }
    return $t({
        defaultMessage: 'Folders {count}',
        description: 'Submenu item label showing the count of selected local workspace folders',
        id: 'fcd57d5a48',
    }, { count: activeDirectories.length });
};
const folderPlusMenuLabel = ($t) => $t({
    defaultMessage: 'Folder',
    description: 'Submenu item label for attaching a local folder',
    id: '306e8dc83f',
});
const WorkspaceDirectoriesChip = () => {
    const explicitDirectories = useExplicitWorkspaceDirectories();
    const { canPickDirectory } = useHandleAddFolder();
    const managedFolders = useManagedFolders();
    const [recentDirectories] = useRecentWorkspaceDirectories();
    const { $t } = useIntl();
    const [isOpen, setIsOpen] = React.useState(false);
    const activeDirectories = useMemo(() => [...managedFolders, ...explicitDirectories], [managedFolders, explicitDirectories]);
    const selectorDirectories = useFolderSelectorDirectories(explicitDirectories, recentDirectories);
    const stableSelectorDirectories = useStableDirectoriesWhileOpen(selectorDirectories, isOpen);
    const unavailablePaths = useUnavailableDirectoryPaths(stableSelectorDirectories);
    const activePath = explicitDirectories[0]?.path;
    const chipLabel = workspaceFolderLabel(activeDirectories, $t);
    if (activeDirectories.length === 0)
        return null;
    return (_jsxs(DropdownMenu, { isOpen: isOpen, onToggle: setIsOpen, triggerElement: _jsx(Button, { variant: "tonal", size: "small", pill: true, leadingAccessory: $icon('folder-open'), children: _jsx("span", { className: "inline-block truncate align-bottom", style: { maxWidth: WORKSPACE_FOLDER_CHIP_MAX_WIDTH_PX }, children: chipLabel }) }), children: [_jsx(ManagedFolderMenuItems, { managedFolders: managedFolders, withTrailingSeparator: managedFolders.length > 0 && stableSelectorDirectories.length > 0 }), _jsx(FolderSelectorMenuItems, { directories: stableSelectorDirectories, activePath: activePath, unavailablePaths: unavailablePaths, withTrailingSeparator: explicitDirectories.length > 0 || canPickDirectory }), explicitDirectories.length > 0 && (_jsx(RemoveWorkspaceFolderMenuItem, { withTrailingSeparator: canPickDirectory })), canPickDirectory && _jsx(WorkspaceFolderMenuItems, { showAddFolder: true })] }));
};
export const ComputerWorkspaceFolderPlusMenuItems = () => {
    const { $t } = useIntl();
    const { canPickDirectory } = useHandleAddFolder();
    const managedFolders = useManagedFolders();
    const explicitDirectories = useExplicitWorkspaceDirectories();
    const [recentDirectories] = useRecentWorkspaceDirectories();
    const [isOpen, setIsOpen] = React.useState(false);
    const selectorDirectories = useFolderSelectorDirectories(explicitDirectories, recentDirectories);
    const stableSelectorDirectories = useStableDirectoriesWhileOpen(selectorDirectories, isOpen);
    const unavailablePaths = useUnavailableDirectoryPaths(stableSelectorDirectories);
    const activePath = explicitDirectories[0]?.path;
    const triggerLabel = folderPlusMenuLabel($t);
    if (!canPickDirectory && stableSelectorDirectories.length === 0 && managedFolders.length === 0) {
        return null;
    }
    return (_jsxs(DropdownMenu.Submenu, { isOpen: isOpen, onToggle: setIsOpen, minWidthPx: 260, triggerElement: _jsx(DropdownMenu.SubmenuItem, { leadingAccessory: $icon('folder-open'), children: _jsx("span", { className: "block max-w-[260px] whitespace-normal break-all text-left", children: triggerLabel }) }), children: [_jsx(ManagedFolderMenuItems, { managedFolders: managedFolders, withTrailingSeparator: managedFolders.length > 0 && stableSelectorDirectories.length > 0 }), _jsx(FolderSelectorMenuItems, { directories: stableSelectorDirectories, activePath: activePath, unavailablePaths: unavailablePaths, withTrailingSeparator: explicitDirectories.length > 0 || canPickDirectory }), stableSelectorDirectories.length === 0 && (_jsx(DropdownMenu.InfoItem, { children: _jsx(FormattedMessage, { defaultMessage: "No recent folders", description: "Empty state shown when no local folders were recently attached", id: "8059a0ea30" }) })), explicitDirectories.length > 0 && (_jsx(RemoveWorkspaceFolderMenuItem, { withTrailingSeparator: canPickDirectory })), canPickDirectory && _jsx(WorkspaceFolderMenuItems, { showAddFolder: true })] }));
};
export const ComputerWorkspaceFolders = () => _jsx(WorkspaceDirectoriesChip, {});
//# sourceMappingURL=ComputerWorkspaceFolders.js.map